import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, User, Mail, CreditCard, Calendar, Lock, Eye, EyeOff, Crown, Sparkles, ExternalLink, Shield } from 'lucide-react';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import LightLogo from '@/components/LightLogo';
import LightBackground from '@/components/LightBackground';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';

const profileSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
});

const passwordSchema = z.object({
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

interface ClientProfile {
  id: string;
  name: string;
  email: string;
  plan: string;
  plan_price: number;
  status: string;
  subscription_start: string;
  subscription_end: string;
}

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { monthlyPrice, lifetimePrice } = useAdminSettings();
  const { user, loading: authLoading, signOut } = useAuth();
  const { 
    hasAccess, 
    subscriptionStatus, 
    createCheckoutSession, 
    openCustomerPortal,
    loading: subscriptionLoading 
  } = useSubscription();
  
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  
  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Require authentication
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('clients')
        .select('id, name, email, plan, plan_price, status, subscription_start, subscription_end')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        toast.error('Failed to load profile');
        setIsLoading(false);
        return;
      }

      if (data) {
        setProfile(data);
        setEditedName(data.name);
      } else {
        // No client record - create freemium profile data from auth user
        const freemiumProfile: ClientProfile = {
          id: '',
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'Member',
          email: user.email || '',
          plan: 'Freemium',
          plan_price: 0,
          status: 'active',
          subscription_start: new Date().toISOString().split('T')[0],
          subscription_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        };
        setProfile(freemiumProfile);
        setEditedName(freemiumProfile.name);
      }
      setIsLoading(false);
    };

    fetchProfile();
  }, [user]);

  const handleSave = async () => {
    const result = profileSchema.safeParse({ name: editedName });
    
    if (!result.success) {
      setNameError(result.error.errors[0]?.message || 'Invalid name');
      return;
    }

    setNameError('');
    setIsSaving(true);

    if (!profile) {
      setIsSaving(false);
      return;
    }

    // If profile has no ID (freemium without client record), we can't save to DB
    if (!profile.id) {
      setProfile({ ...profile, name: editedName });
      toast.success('Profile updated locally');
      setIsSaving(false);
      return;
    }

    const { error } = await supabase
      .from('clients')
      .update({ name: editedName })
      .eq('id', profile.id);

    if (error) {
      toast.error('Failed to update profile');
    } else {
      setProfile({ ...profile, name: editedName });
      toast.success('Profile updated successfully');
    }

    setIsSaving(false);
  };

  const handlePasswordChange = async () => {
    const result = passwordSchema.safeParse({ newPassword, confirmPassword });
    
    if (!result.success) {
      setPasswordError(result.error.errors[0]?.message || 'Invalid password');
      return;
    }

    setPasswordError('');
    setIsChangingPassword(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      setPasswordError(error.message);
      toast.error('Failed to update password');
    } else {
      toast.success('Password updated successfully');
      setNewPassword('');
      setConfirmPassword('');
    }

    setIsChangingPassword(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600';
      case 'expiring':
        return 'text-amber-600';
      case 'expired':
        return 'text-red-600';
      default:
        return 'text-slate-500';
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-blue-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-slate-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/" className="flex items-center">
          <LightLogo size="md" />
        </Link>
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-600 hover:text-blue-600 hover:bg-blue-50"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Your Profile
          </h1>
          <p className="text-slate-600">
            Manage your personal information
          </p>
        </motion.div>

        {profile ? (
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Personal Information */}
            <div className="p-6 bg-white rounded-2xl shadow-lg border border-slate-100">
              <h2 className="text-lg font-semibold tracking-wider mb-6 text-slate-900 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Personal Information
              </h2>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 text-sm font-medium">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 text-slate-900 h-12 rounded-xl"
                    placeholder="Your name"
                  />
                  {nameError && <p className="text-red-500 text-xs">{nameError}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Address
                  </Label>
                  <Input
                    value={profile.email}
                    disabled
                    className="bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed h-12 rounded-xl"
                  />
                  <p className="text-xs text-slate-400">
                    Contact support to change your email address
                  </p>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={isSaving || editedName === profile.name}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>

            {/* Subscription Information */}
            <div className="p-6 bg-white rounded-2xl shadow-lg border border-slate-100">
              <h2 className="text-lg font-semibold tracking-wider mb-6 text-slate-900 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-600" />
                Subscription
              </h2>

              {profile.plan?.toLowerCase() === 'freemium' ? (
                /* Freemium User - Show Upgrade Options */
                <div className="space-y-6">
                  <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-5 w-5 text-blue-600" />
                      <span className="text-slate-900 font-medium">Free Account</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      You're currently on the free plan. Upgrade to unlock full access to all modules and classes!
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Monthly Plan */}
                    <div className="p-5 rounded-xl bg-white border border-slate-200 hover:border-blue-300 transition-colors shadow-sm">
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Monthly</h3>
                      <p className="text-3xl font-bold text-blue-600 mb-1">${monthlyPrice}</p>
                      <p className="text-xs text-slate-500 mb-4">per month</p>
                      <ul className="text-sm text-slate-600 space-y-2 mb-4">
                        <li className="flex items-center gap-2">
                          <span className="text-blue-600">✦</span>
                          Module 1 unlocked initially
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-blue-600">✦</span>
                          Unlock next module on completion
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-blue-600">✦</span>
                          Cancel anytime
                        </li>
                      </ul>
                      <Button
                        onClick={async () => {
                          setIsSubscribing(true);
                          try {
                            await createCheckoutSession('monthly');
                          } catch (error: any) {
                            toast.error(error.message || 'Failed to start checkout');
                          } finally {
                            setIsSubscribing(false);
                          }
                        }}
                        disabled={isSubscribing}
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white rounded-xl"
                        variant="default"
                      >
                        {isSubscribing ? 'Loading...' : 'Subscribe Monthly'}
                      </Button>
                    </div>

                    {/* Lifetime Plan */}
                    <div className="p-5 rounded-xl bg-white border-2 border-blue-300 relative overflow-hidden shadow-md">
                      <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs px-3 py-1 rounded-bl font-medium">
                        BEST VALUE
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <Crown className="h-5 w-5 text-blue-600" />
                        Lifetime
                      </h3>
                      <p className="text-3xl font-bold text-blue-600 mb-1">${lifetimePrice}</p>
                      <p className="text-xs text-slate-500 mb-4">one-time payment</p>
                      <ul className="text-sm text-slate-600 space-y-2 mb-4">
                        <li className="flex items-center gap-2">
                          <span className="text-blue-600">✦</span>
                          All 50 modules unlocked
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-blue-600">✦</span>
                          Lifetime access
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-blue-600">✦</span>
                          Future updates included
                        </li>
                      </ul>
                      <Button
                        onClick={async () => {
                          setIsSubscribing(true);
                          try {
                            await createCheckoutSession('lifetime');
                          } catch (error: any) {
                            toast.error(error.message || 'Failed to start checkout');
                          } finally {
                            setIsSubscribing(false);
                          }
                        }}
                        disabled={isSubscribing}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                      >
                        {isSubscribing ? 'Loading...' : 'Get Lifetime Access'}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Paid User - Show Subscription Details */
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs tracking-wider text-slate-500 uppercase mb-1">Plan</p>
                      <p className="text-lg font-semibold text-blue-600">{profile.plan}</p>
                    </div>

                    <div>
                      <p className="text-xs tracking-wider text-slate-500 uppercase mb-1">Status</p>
                      <p className={`text-lg font-semibold capitalize ${getStatusColor(profile.status)}`}>
                        {profile.status}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs tracking-wider text-slate-500 uppercase mb-1">Price</p>
                      <p className="text-lg font-semibold text-slate-900">
                        ${profile.plan_price.toLocaleString()}
                        {profile.plan !== 'Lifetime' && <span className="text-sm text-slate-500">/month</span>}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs tracking-wider text-slate-500 uppercase mb-1">Member Since</p>
                      <p className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-600/60" />
                        {formatDate(profile.subscription_start)}
                      </p>
                    </div>

                    {profile.plan !== 'Lifetime' && (
                      <div className="md:col-span-2">
                        <p className="text-xs tracking-wider text-slate-500 uppercase mb-1">
                          {profile.status === 'expired' ? 'Expired On' : 'Renews On'}
                        </p>
                        <p className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-600/60" />
                          {formatDate(profile.subscription_end)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Billing Management */}
                  <div className="pt-4 border-t border-slate-200">
                    <Button
                      onClick={async () => {
                        try {
                          await openCustomerPortal();
                        } catch (error: any) {
                          if (error?.message?.includes('No Stripe customer found')) {
                            toast.error('Billing management is not available for your account type.');
                          } else {
                            toast.error('Failed to open billing portal.');
                          }
                        }
                      }}
                      className="bg-slate-800 hover:bg-slate-900 text-white rounded-xl"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Manage Billing
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Password Change */}
            <div className="p-6 bg-white rounded-2xl shadow-lg border border-slate-100">
              <h2 className="text-lg font-semibold tracking-wider mb-6 text-slate-900 flex items-center gap-2">
                <Lock className="h-5 w-5 text-blue-600" />
                Change Password
              </h2>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-700 text-sm font-medium">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-slate-50 border-slate-200 focus:border-blue-500 text-slate-900 pr-10 h-12 rounded-xl"
                      placeholder="Min. 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700 text-sm font-medium">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-slate-50 border-slate-200 focus:border-blue-500 text-slate-900 pr-10 h-12 rounded-xl"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordError && <p className="text-red-500 text-xs">{passwordError}</p>}
                </div>

                <Button
                  onClick={handlePasswordChange}
                  disabled={isChangingPassword || !newPassword || !confirmPassword}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                >
                  {isChangingPassword ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </div>

            {/* Sign Out */}
            <div className="text-center pt-4">
              <Button
                variant="ghost"
                onClick={async () => { await signOut(); navigate('/'); }}
                className="text-slate-500 hover:text-red-600 hover:bg-red-50"
              >
                Sign Out
              </Button>
            </div>

            {/* Trust indicator */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Shield className="w-4 h-4 text-green-500" />
              <span>Your information is secure</span>
            </div>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
};

export default Profile;